"use client";

import * as React from "react";
import { Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-md border border-border bg-accent/30 p-8 text-center">
        <div className="flex size-12 items-center justify-center rounded-lg bg-secondary/10">
          <svg className="size-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-lg font-default font-medium text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Make Another Inquiry
        </Button>
      </div>;
  }
  const inputClasses = "flex h-10 w-full rounded-md border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-base shadow-none transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-secondary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-default";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Your Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-primary">Full Name</FormLabel>
              <FormControl>
                <input type="text" className={inputClasses} placeholder="Julian Vane" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs">Please provide your full legal name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-primary">Email Address</FormLabel>
              <FormControl>
                <input type="email" className={inputClasses} placeholder="julian@estate.com" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs">For your reservation confirmation.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-primary">Phone Number</FormLabel>
            <FormControl>
              <input type="tel" className={inputClasses} placeholder="+1 (555) 000-0000" {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs">Optional – for day-of coordination.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-primary">Inquiry Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your preferred dates and guest count..." className="min-h-[120px] resize-y rounded-md border-input bg-transparent font-default focus-visible:ring-secondary" {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs">Include any dietary preferences or special occasions.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-secondary transition-all duration-300 shadow-md" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Processing...
          </> : "Submit Inquiry"}
      </Button>
    </Form>;
}
export default function ReserveYourVisit() {
  return <section data-section-id="236241" id="reserve-your-visit" className="bg-background text-foreground py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          {}
          <div className="flex flex-col justify-center space-y-10">
            <div className="space-y-4">
              <h2 className="font-serif text-5xl font-bold tracking-wider text-primary lg:text-7xl">
                Reserve Your Visit
              </h2>
              <p className="max-w-md text-lg leading-relaxed text-muted-foreground font-default">
                Experience the artistry of Velvet Vineyards. Whether you're booking a private tasting or a tour of our historic cellars, we invite you to the estate.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-secondary">
                  Concierge
                </p>
                <Link to="tel:+15558889999" className="font-serif text-3xl font-semibold text-primary transition-colors hover:text-secondary lg:text-4xl">
                  (555) 888-9999
                </Link>
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-secondary">
                  The Estate
                </p>
                <address className="text-lg not-italic text-foreground font-default leading-relaxed">
                  1200 Oenology Way, Napa Valley<br />
                  St. Helena, CA 94574
                </address>
              </div>
              
              <div>
                <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-secondary">
                  Tasting Hours
                </p>
                <p className="text-lg text-foreground font-default">
                  Daily: 10 am — 6 pm
                </p>
              </div>
            </div>
          </div>

          {}
          <div className="rounded-md border-none bg-card p-6 shadow-lg sm:p-8 lg:p-10">
            <div className="mb-8">
              <h3 className="font-serif text-2xl font-semibold text-primary tracking-wide">
                Inquiry Form
              </h3>
              <p className="mt-2 text-sm text-muted-foreground font-default">
                Complete the details below and our estate manager will contact you to finalize your reservation.
              </p>
            </div>

            <FormProvider formKey="reserve_visit_tasting_tours" formId="reserve_visit_tasting_tours" sectionName="reserve-your-visit" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} submitText="Submit Inquiry" successMessage="Your inquiry has been received. We look forward to welcoming you to the estate.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}